import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { foodService } from '../../../services/foodService';

const FoodCart = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [], vendorId: null, cityId: null });
  const [vendorData, setVendorData] = useState(null);
  const [totals, setTotals] = useState({ productsTotal: 0, deliveryFee: 3.00, orderTotal: 0, itemCount: 0 });

  useEffect(() => {
    loadCart();
  }, []);

  const loadCart = async () => {
    const currentCart = foodService?.getCart();
    setCart(currentCart);

    if (currentCart?.vendorId) {
      try {
        const vendor = await foodService?.getVendorById(currentCart?.vendorId);
        setVendorData(vendor);
      } catch (err) {
        console.error('Error loading vendor:', err);
      }
    }

    const calculatedTotals = foodService?.calculateCartTotals();
    setTotals(calculatedTotals);
  };

  const handleUpdateQuantity = (productId, newQuantity) => {
    foodService?.updateCartItemQuantity(productId, newQuantity);
    loadCart();
  };

  const handleRemoveItem = (productId) => {
    foodService?.removeFromCart(productId);
    loadCart();
  };

  const handleClearCart = () => {
    if (window.confirm('Vuoi svuotare il carrello?')) {
      foodService?.clearCart();
      loadCart();
    }
  };

  const handleProceedToCheckout = () => {
    navigate('/food/checkout');
  };

  if (cart?.items?.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="ShoppingCart" size={48} className="text-muted-foreground" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">Carrello vuoto</h2>
            <p className="text-muted-foreground mb-6">Aggiungi prodotti per iniziare il tuo ordine</p>
            <button
              onClick={() => navigate('/food')}
              className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              Esplora esercenti
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-32">
      {/* Header */}
      <div className="bg-card shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-4"
          >
            <Icon name="ArrowLeft" size={20} />
            <span>Continua lo shopping</span>
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-1">Il tuo carrello</h1>
              {vendorData && (
                <p className="text-muted-foreground">Ordine da {vendorData?.name}</p>
              )}
            </div>
            <button
              onClick={handleClearCart}
              className="text-sm text-destructive hover:text-destructive/80 transition-colors flex items-center gap-2"
            >
              <Icon name="Trash2" size={16} />
              <span>Svuota carrello</span>
            </button>
          </div>
        </div>
      </div>
      {/* Cart Items */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="space-y-4 mb-8">
          {cart?.items?.map((item) => (
            <div
              key={item?.id}
              className="bg-card border border-border rounded-xl p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-foreground mb-1">{item?.name}</h3>
                  {item?.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{item?.description}</p>
                  )}
                  <p className="text-primary font-semibold">€{item?.price?.toFixed(2)}</p>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 bg-muted rounded-lg">
                    <button
                      onClick={() => handleUpdateQuantity(item?.id, item?.quantity - 1)}
                      className="w-8 h-8 flex items-center justify-center hover:bg-muted-foreground/10 rounded-l-lg transition-colors"
                      disabled={item?.quantity <= 1}
                    >
                      <Icon name="Minus" size={16} />
                    </button>
                    <span className="w-8 text-center font-semibold">{item?.quantity}</span>
                    <button
                      onClick={() => handleUpdateQuantity(item?.id, item?.quantity + 1)}
                      className="w-8 h-8 flex items-center justify-center hover:bg-muted-foreground/10 rounded-r-lg transition-colors"
                    >
                      <Icon name="Plus" size={16} />
                    </button>
                  </div>

                  <button
                    onClick={() => handleRemoveItem(item?.id)}
                    className="w-8 h-8 flex items-center justify-center text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                  >
                    <Icon name="Trash2" size={16} />
                  </button>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-border flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Subtotale</span>
                <span className="text-lg font-bold text-foreground">€{(item?.price * item?.quantity)?.toFixed(2)}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Order Summary */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h2 className="text-xl font-bold text-foreground mb-4">Riepilogo ordine</h2>
          <div className="space-y-3">
            <div className="flex justify-between text-foreground">
              <span>Totale prodotti</span>
              <span className="font-semibold">€{totals?.productsTotal?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-foreground">
              <span>Costo consegna</span>
              <span className="font-semibold">€{totals?.deliveryFee?.toFixed(2)}</span>
            </div>
            <div className="pt-3 border-t border-border flex justify-between text-lg">
              <span className="font-bold text-foreground">Totale ordine</span>
              <span className="font-bold text-primary">€{totals?.orderTotal?.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
      {/* Fixed Checkout Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border shadow-2xl z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <button
            onClick={handleProceedToCheckout}
            className="w-full bg-primary text-primary-foreground rounded-xl px-6 py-4 hover:bg-primary/90 transition-all duration-300 flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <Icon name="CreditCard" size={24} />
              <span className="font-semibold text-lg">Procedi al pagamento</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-xl">€{totals?.orderTotal?.toFixed(2)}</span>
              <Icon name="ChevronRight" size={24} />
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default FoodCart;